import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { RequisitionApprovalNotice } from "../components/RequisitionApprovalNotice";
import { useSubmitRequisition, useReopenRequisition, useDeleteRequisition } from "../hooks/useRequisitions";
import { RequisitionApproveModal, type RequisitionApproveTarget } from "../components/RequisitionApproveModal";
import apiClient from "@/api/client";
import { useInfiniteFeed } from "@/api/useInfiniteFeed";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { LoadMoreButton } from "@/components/ui/LoadMoreButton";
import { Modal } from "@/components/ui/Modal";
import { FilterMultiSelect } from "@/components/ui/FilterMultiSelect";
import { PageLoader } from "@/components/ui/Spinner";
import { RangeFilter } from "@/components/ui/RangeFilter";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { VisibleAmountSum } from "@/components/ui/VisibleAmountSum";
import { XlsxExportButton } from "@/components/ui/XlsxExportButton";
import { requisitionsApi } from "../api/requisitions";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { useAuthStore } from "@/store/auth";
import { formatDate } from "@/utils/formatDate";
import {
  ORDER_STATUS_LABELS,
  PRIORITY_LABELS,
  REQUISITION_STATUS_LABELS,
  type Requisition,
  type OrderStatus,
  type RequisitionStatus,
} from "../types";

const statusColors: Record<RequisitionStatus, "gray" | "blue" | "green" | "red"> = {
  draft: "gray", submitted: "blue", approved: "green", rejected: "red",
};

const orderStatusColors: Record<OrderStatus, "gray" | "blue" | "green" | "red" | "yellow" | "purple" | "orange"> = {
  sent: "blue", accepted: "green", rejected: "red", in_progress: "orange",
  in_progress_partial: "yellow", submitted_for_acceptance: "purple",
  accepted_final: "green", closed: "gray", cancelled: "gray",
};

// Filtr „Status zamówienia" — „none" to zapotrzebowania, z których zamówienie nie powstało.
const ORDER_STATUS_FILTER_OPTIONS = [
  { value: "none", label: "Brak zamówienia" },
  ...(Object.entries(ORDER_STATUS_LABELS) as [OrderStatus, string][]).map(([value, label]) => ({
    value,
    label,
  })),
];

const SELECT_CLS =
  "text-xs border border-gray-200 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]";

// Słowniki do filtrów. Te same listy, po których filtruje lista zamówień — wolno się
// zmieniają, więc trzymamy je 5 minut zamiast dociągać przy każdym otwarciu.
interface ProjectFilterOption {
  id: string;
  name: string;
  short_name: string;
  is_active: boolean;
}

interface ContractorFilterOption {
  id: string;
  name: string;
  short_name: string;
  is_active: boolean;
}

function useProjectFilterOptions() {
  return useQuery({
    queryKey: ["orders-filter-projects"],
    queryFn: () =>
      apiClient.get<{ data: ProjectFilterOption[] }>("/projects/").then((r) => r.data.data),
    staleTime: 5 * 60_000,
  });
}

function useContractorFilterOptions() {
  return useQuery({
    queryKey: ["orders-filter-contractors"],
    queryFn: () =>
      apiClient.get<{ data: ContractorFilterOption[] }>("/contractors/").then((r) => r.data.data),
    staleTime: 5 * 60_000,
  });
}

// Descriptions can be rich text (HTML) — strip tags for the truncated list preview.
function stripHtml(html: string): string {
  if (!html.trimStart().startsWith("<")) return html;
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

// Server-side sort fields (?ordering= on the paginated feed — a client-side sort
// would only ever reorder the 50 rows already loaded). Mirrors OrdersListPage.
type SortField =
  | "project__name"
  | "reporter__last_name"
  | "created_at"
  | "priority"
  | "status"
  | "deadline";
type SortDir = "asc" | "desc";

function SortableTh({
  field, label, sortField, sortDir, onSort,
}: {
  field: SortField;
  label: string;
  sortField: SortField | null;
  sortDir: SortDir;
  onSort: (f: SortField) => void;
}) {
  const active = sortField === field;
  return (
    <th
      onClick={() => onSort(field)}
      className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200 whitespace-nowrap"
    >
      {label}{" "}
      <span className={active ? "text-[var(--color-primary)]" : "opacity-30"}>
        {active ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
      </span>
    </th>
  );
}

export function RequisitionsListPage() {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  const navigate = useNavigate();

  // Świeżo utworzone zapotrzebowanie (stan nawigacji z formularza) → duży czerwony
  // banner „sprawdź i zaakceptuj". Stan historii czyścimy od razu, żeby odświeżenie
  // strony nie pokazywało bannera ponownie.
  const [justCreated, setJustCreated] = useState(
    () => !!(location.state as { created?: boolean } | null)?.created,
  );
  useEffect(() => {
    if ((location.state as { created?: boolean } | null)?.created) {
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Odrzucone zaśmiecają listę (zostają na zawsze) — domyślnie ukryte. Ukrywa je SERWER:
  // odsianie ich na froncie działałoby tylko na doczytanych 50 wierszach, więc „Pokaż
  // odrzucone" gubiłoby wszystko poza pierwszą porcją.
  const [showRejected, setShowRejected] = useState(false);
  // Szukajka też serwerowa — po całym zbiorze zapotrzebowań, nie po widocznej porcji.
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Filtry — wszystkie serwerowe, z tego samego powodu co szukajka: front trzyma tylko
  // doczytaną porcję, więc filtrowanie u niego zawężałoby wycinek, a nie całą bazę.
  const [filterStatus, setFilterStatus] = useState<RequisitionStatus | "">("");
  const [filterOrderStatus, setFilterOrderStatus] = useState<string[]>([]);
  const [filterProject, setFilterProject] = useState("");
  const [filterContractor, setFilterContractor] = useState("");
  const [filterReporter, setFilterReporter] = useState("");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [deadlineFrom, setDeadlineFrom] = useState("");
  const [deadlineTo, setDeadlineTo] = useState("");
  // Kwoty wpisuje się cyfra po cyfrze — bez odczekania każdy znak byłby osobnym
  // zapytaniem po całej bazie (tak jak w szukajce wyżej).
  const [valueMinInput, setValueMinInput] = useState("");
  const [valueMaxInput, setValueMaxInput] = useState("");
  const [valueMin, setValueMin] = useState("");
  const [valueMax, setValueMax] = useState("");
  useEffect(() => {
    const t = setTimeout(() => {
      setValueMin(valueMinInput.trim());
      setValueMax(valueMaxInput.trim());
    }, 400);
    return () => clearTimeout(t);
  }, [valueMinInput, valueMaxInput]);

  const { data: projectOptions = [] } = useProjectFilterOptions();
  const { data: contractorOptions = [] } = useContractorFilterOptions();
  // Pracownik widzi wyłącznie swoje zapotrzebowania (filtr po stronie API), więc filtr
  // „Zgłaszający" nie miałby dla niego czego zawęzić — nie pokazujemy go i nie pobieramy listy.
  const canFilterByReporter = user?.role !== "employee";
  const { data: userOptions = [] } = useInternalUsers({ enabled: canFilterByReporter });

  const sortedProjects = useMemo(
    () =>
      [...projectOptions].sort((a, b) =>
        (a.short_name || a.name).localeCompare(b.short_name || b.name, "pl"),
      ),
    [projectOptions],
  );
  const sortedContractors = useMemo(
    () => [...contractorOptions].sort((a, b) => a.name.localeCompare(b.name, "pl")),
    [contractorOptions],
  );
  const sortedReporters = useMemo(
    () =>
      [...userOptions].sort((a, b) =>
        (a.full_name || a.username).localeCompare(b.full_name || b.username, "pl"),
      ),
    [userOptions],
  );

  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  // Filtry inne niż status — także dla licznika ukrytych odrzuconych, żeby „(n)" przy
  // checkboxie liczyło to, co po ich odsłonięciu naprawdę dojdzie do listy.
  const scopeFilters: Record<string, string> = {};
  if (search) scopeFilters.q = search;
  if (filterProject) scopeFilters.project = filterProject;
  // Posortowane, żeby ten sam wybór w innej kolejności nie był osobnym kluczem zapytania.
  if (filterOrderStatus.length) scopeFilters.order_status = [...filterOrderStatus].sort().join(",");
  if (filterContractor) scopeFilters.contractor = filterContractor;
  if (filterReporter) scopeFilters.reporter = filterReporter;
  if (createdFrom) scopeFilters.created_after = createdFrom;
  if (createdTo) scopeFilters.created_before = createdTo;
  if (deadlineFrom) scopeFilters.deadline_after = deadlineFrom;
  if (deadlineTo) scopeFilters.deadline_before = deadlineTo;
  if (valueMin) scopeFilters.value_min = valueMin;
  if (valueMax) scopeFilters.value_max = valueMax;

  const filters: Record<string, string> = { ...scopeFilters };
  // Wybrany status wygrywa z domyślnym ukryciem odrzuconych — inaczej „Status: Odrzucone"
  // dawałoby pustą listę.
  if (filterStatus) filters.status = filterStatus;
  else if (!showRejected) filters.exclude_status = "rejected";
  if (sortField) filters.ordering = sortDir === "desc" ? `-${sortField}` : sortField;

  const hasActiveFilters =
    !!filterStatus ||
    filterOrderStatus.length > 0 ||
    !!filterProject ||
    !!filterContractor ||
    !!filterReporter ||
    !!createdFrom ||
    !!createdTo ||
    !!deadlineFrom ||
    !!deadlineTo ||
    !!valueMinInput ||
    !!valueMaxInput ||
    !!searchInput;

  const clearFilters = () => {
    setFilterStatus("");
    setFilterOrderStatus([]);
    setFilterProject("");
    setFilterContractor("");
    setFilterReporter("");
    setCreatedFrom("");
    setCreatedTo("");
    setDeadlineFrom("");
    setDeadlineTo("");
    setValueMinInput("");
    setValueMaxInput("");
    setSearchInput("");
  };

  const {
    items: visible,
    isLoading,
    total,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    fetchAll,
    isFetchingAll,
  } = useInfiniteFeed<Requisition>({
    key: ["requisitions", "feed"],
    url: "/requisitions/feed/",
    filters,
  });
  // Licznik ukrytych odrzuconych — osobne, najtańsze możliwe zapytanie (tylko meta.count).
  // Przy wybranym statusie nic nie jest ukrywane, więc nie ma czego liczyć.
  const { total: rejectedCount } = useInfiniteFeed<Requisition>({
    key: ["requisitions", "feed", "rejected-count"],
    url: "/requisitions/feed/",
    filters: { status: "rejected", ...scopeFilters },
    pageSize: 1,
    enabled: !showRejected && !filterStatus,
  });
  const submitMutation = useSubmitRequisition();
  const reopenMutation = useReopenRequisition();
  const deleteMutation = useDeleteRequisition();

  const canDelete = user?.is_ceo || (user?.role === "director" && user?.is_technical_director);

  const hiddenRejectedCount = showRejected || filterStatus ? 0 : rejectedCount;

  const [approveModal, setApproveModal] = useState<RequisitionApproveTarget | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const handleApproveOpen = (id: string, deadline: string | null, suggestedOrderType: string | null) => {
    setApproveModal({ id, deadline, suggestedOrderType });
  };

  return (
    <div>
      {justCreated && (
        <div className="relative mb-6">
          <RequisitionApprovalNotice />
          <button
            type="button"
            onClick={() => setJustCreated(false)}
            title="Zamknij"
            className="absolute right-2 top-2 text-red-800 hover:text-red-950 dark:text-red-300 dark:hover:text-red-100"
          >
            ✕
          </button>
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Zapotrzebowania</h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1" />
        </div>
        <div className="flex items-center gap-4 self-start sm:self-auto flex-wrap">
          <div className="relative">
            <Search
              size={14}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none"
            />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Szukaj: przedsięwzięcie, opis, działka…"
              title="Szuka po całej bazie zapotrzebowań — przedsięwzięcie, opis, zgłaszający, podwykonawca, nr zamówienia i miejscowość działki"
              className="text-xs border border-gray-200 dark:border-gray-600 rounded pl-7 pr-2 py-1.5 w-64 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
            />
          </div>
          <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 cursor-pointer select-none whitespace-nowrap">
            <input
              type="checkbox"
              className="rounded border-gray-300 dark:border-gray-600"
              checked={showRejected}
              onChange={(e) => setShowRejected(e.target.checked)}
            />
            Pokaż odrzucone{hiddenRejectedCount > 0 && ` (${hiddenRejectedCount})`}
          </label>
          {/* Arkusz ze WSZYSTKIMI zapotrzebowaniami spełniającymi bieżące filtry — serwer
              dostaje te same parametry co feed, więc nie tylko doczytana porcja. */}
          <XlsxExportButton
            label="Pobierz zapotrzebowania (.xlsx)"
            title="Pobierz wszystkie odfiltrowane zapotrzebowania (nie tylko doczytane) jako arkusz Excel"
            count={total ?? visible.length}
            disabled={isLoading}
            download={() => requisitionsApi.downloadListXlsx(filters)}
          />
          <Link to="/requisitions/new">
            <Button><Plus size={16} />Nowe zapotrzebowanie</Button>
          </Link>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as RequisitionStatus | "")}
          className={SELECT_CLS}
          title="Status zapotrzebowania"
        >
          <option value="">Wszystkie statusy</option>
          {(Object.entries(REQUISITION_STATUS_LABELS) as [RequisitionStatus, string][]).map(
            ([val, label]) => (
              <option key={val} value={val}>{label}</option>
            ),
          )}
        </select>
        <FilterMultiSelect
          label="Status zamówienia"
          options={ORDER_STATUS_FILTER_OPTIONS}
          selected={filterOrderStatus}
          onChange={setFilterOrderStatus}
        />
        <SearchableSelect
          placeholder="Wszystkie przedsięwzięcia"
          value={filterProject}
          onChange={(e) => setFilterProject(e.target.value)}
          options={[
            { value: "", label: "Wszystkie przedsięwzięcia" },
            ...sortedProjects.map((p) => ({
              value: p.id,
              label: `${p.short_name} — ${p.name}${p.is_active ? "" : " [nieaktywne]"}`,
            })),
          ]}
        />
        <SearchableSelect
          placeholder="Wszyscy podwykonawcy"
          value={filterContractor}
          onChange={(e) => setFilterContractor(e.target.value)}
          options={[
            { value: "", label: "Wszyscy podwykonawcy" },
            ...sortedContractors.map((c) => ({
              value: c.id,
              label: c.short_name ? `${c.short_name} — ${c.name}` : c.name,
            })),
          ]}
        />
        {canFilterByReporter && (
          <SearchableSelect
            placeholder="Wszyscy zgłaszający"
            value={filterReporter}
            onChange={(e) => setFilterReporter(e.target.value)}
            options={[
              { value: "", label: "Wszyscy zgłaszający" },
              ...sortedReporters.map((u) => ({
                value: u.id,
                label: u.full_name || u.username,
              })),
            ]}
          />
        )}
        <RangeFilter
          label="Wartość"
          type="number"
          step={1000}
          from={valueMinInput}
          to={valueMaxInput}
          onFrom={setValueMinInput}
          onTo={setValueMaxInput}
          fromTitle="Wartość netto od [PLN] — suma pozycji, do których masz uprawnienia finansowe"
          toTitle="Wartość netto do [PLN] — suma pozycji, do których masz uprawnienia finansowe"
          idleBorderClassName="border-gray-200 dark:border-gray-600"
          inputClassName={`${SELECT_CLS} w-[90px]`}
        />
        <RangeFilter
          label="Zgłoszono"
          type="date"
          from={createdFrom}
          to={createdTo}
          onFrom={setCreatedFrom}
          onTo={setCreatedTo}
          fromTitle="Data zgłoszenia od"
          toTitle="Data zgłoszenia do"
          idleBorderClassName="border-gray-200 dark:border-gray-600"
          inputClassName={`${SELECT_CLS} min-w-[130px]`}
        />
        <RangeFilter
          label="Termin"
          type="date"
          from={deadlineFrom}
          to={deadlineTo}
          onFrom={setDeadlineFrom}
          onTo={setDeadlineTo}
          fromTitle="Termin realizacji od"
          toTitle="Termin realizacji do"
          idleBorderClassName="border-gray-200 dark:border-gray-600"
          inputClassName={`${SELECT_CLS} min-w-[130px]`}
        />
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 underline"
          >
            Wyczyść filtry
          </button>
        )}
      </div>

      {isLoading ? (
        <PageLoader />
      ) : visible.length === 0 && !hasNextPage ? (
        <div className="bg-white dark:bg-gray-800 p-8 text-center text-gray-400 dark:text-gray-500 text-sm">
          {hiddenRejectedCount > 0
            ? `Brak zapotrzebowań — ukryto ${hiddenRejectedCount} odrzuconych`
            : "Brak zapotrzebowań"}
        </div>
      ) : (
        <>
          {/* Mobile card list */}
          <div className="sm:hidden flex flex-col divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {visible.map((req) => (
              <div key={req.id} className={`px-4 py-3 ${req.status === "rejected" ? "opacity-60" : ""}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/requisitions/${req.id}`}
                      className="font-medium text-[var(--color-primary)] hover:underline block truncate"
                    >
                      {req.project_name}
                    </Link>
                    {req.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{stripHtml(req.description)}</p>
                    )}
                    <div className="flex items-center gap-1.5 flex-wrap mt-1">
                      <Badge color={statusColors[req.status]}>{REQUISITION_STATUS_LABELS[req.status]}</Badge>
                      {req.status === "approved" && req.order_status && (
                        <Badge color={orderStatusColors[req.order_status]}>{ORDER_STATUS_LABELS[req.order_status]}</Badge>
                      )}
                      {req.pock_count > 0 && (
                        <Badge color={req.pock_awaiting_count > 0 ? "yellow" : "gray"}>
                          POCK: {req.pock_count}{req.pock_awaiting_count > 0 && ` · ${req.pock_awaiting_count} oczekuje`}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {req.reporter_name} · {new Date(req.created_at).toLocaleDateString("pl-PL")}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Wartość: <VisibleAmountSum values={req.line_items.map((li) => li.total_value)} />
                    </p>
                    {req.deadline && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Termin: {formatDate(req.deadline)}</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                    <Badge color={req.priority === "high" ? "red" : req.priority === "low" ? "gray" : "blue"}>
                      {PRIORITY_LABELS[req.priority]}
                    </Badge>
                    <div className="flex flex-wrap gap-1.5 justify-end">
                      {req.status === "draft" && (
                        <Button size="sm" variant="secondary" loading={submitMutation.isPending} onClick={() => submitMutation.mutate(req.id)}>
                          Zgłoś
                        </Button>
                      )}
                      {req.status === "submitted" && user?.has_orderer_permission && (
                        <Button size="sm" onClick={() => handleApproveOpen(req.id, req.deadline, req.suggested_order_type)}>
                          Zatwierdź
                        </Button>
                      )}
                      {req.status === "rejected" && user?.has_orderer_permission && (
                        <Button
                          size="sm"
                          variant="secondary"
                          title="Cofnij odrzucenie — przywróć zapotrzebowanie do stanu sprzed odrzucenia"
                          loading={reopenMutation.isPending && reopenMutation.variables === req.id}
                          onClick={() => reopenMutation.mutate(req.id)}
                        >
                          Przywróć
                        </Button>
                      )}
                      {req.status === "approved" && req.order_id && (
                        <Link to={`/orders/${req.order_id}`} className="text-xs text-green-600 hover:underline">
                          Zamówienie
                        </Link>
                      )}
                      {canDelete && !req.order_id && (
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => setDeleteConfirmId(req.id)}
                        >
                          Usuń
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* Desktop table */}
          <div className="hidden sm:block bg-white dark:bg-gray-800">
            <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[720px]">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Lp.</th>
                  <SortableTh field="project__name" label="Przedsięwzięcie" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Opis</th>
                  <SortableTh field="reporter__last_name" label="Zgłaszający" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh field="created_at" label="Data zgłoszenia" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh field="priority" label="Priorytet" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh field="status" label="Status" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400" title="Wartość netto pozycji — suma widocznych dla Ciebie kwot">Wartość</th>
                  <SortableTh field="deadline" label="Termin" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Akcje</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visible.map((req, idx) => (
                  <tr
                    key={req.id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 border-l-2 border-transparent hover:border-[var(--color-primary)] transition-colors ${req.status === "rejected" ? "opacity-60" : ""}`}
                  >
                    <td className="px-4 py-3 text-gray-400 dark:text-gray-500">{idx + 1}</td>
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                      <Link
                        to={`/requisitions/${req.id}`}
                        className="hover:text-[var(--color-primary)] hover:underline"
                      >
                        {req.project_name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 max-w-[220px] truncate">{stripHtml(req.description)}</td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">{req.reporter_name}</td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {new Date(req.created_at).toLocaleDateString("pl-PL")}
                    </td>
                    <td className="px-4 py-3">
                      <Badge color={req.priority === "high" ? "red" : req.priority === "low" ? "gray" : "blue"}>
                        {PRIORITY_LABELS[req.priority]}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-gray-400 dark:text-gray-500 w-32 shrink-0 whitespace-nowrap">Zapotrzebowanie</span>
                          <Badge color={statusColors[req.status]}>{REQUISITION_STATUS_LABELS[req.status]}</Badge>
                        </div>
                        {req.status === "approved" && req.order_status && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-gray-400 dark:text-gray-500 w-32 shrink-0 whitespace-nowrap">Zamówienie</span>
                            <Badge color={orderStatusColors[req.order_status]}>
                              {ORDER_STATUS_LABELS[req.order_status]}
                            </Badge>
                          </div>
                        )}
                        {req.pock_count > 0 && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-gray-400 dark:text-gray-500 w-32 shrink-0 whitespace-nowrap">POCK</span>
                            <Badge color={req.pock_awaiting_count > 0 ? "yellow" : "gray"}>
                              {req.pock_count} {req.pock_count === 1 ? "protokół" : req.pock_count < 5 ? "protokoły" : "protokołów"}
                              {req.pock_awaiting_count > 0 && ` · ${req.pock_awaiting_count} oczekuje`}
                            </Badge>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                      <VisibleAmountSum values={req.line_items.map((li) => li.total_value)} />
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{formatDate(req.deadline)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        {req.status === "draft" && (
                          <Button
                            size="sm"
                            variant="secondary"
                            loading={submitMutation.isPending}
                            onClick={() => submitMutation.mutate(req.id)}
                          >
                            Zgłoś
                          </Button>
                        )}
                        {req.status === "submitted" && user?.has_orderer_permission && (
                          <Button
                            size="sm"
                            onClick={() => handleApproveOpen(req.id, req.deadline, req.suggested_order_type)}
                          >
                            Zatwierdź
                          </Button>
                        )}
                        {req.status === "rejected" && user?.has_orderer_permission && (
                          <Button
                            size="sm"
                            variant="secondary"
                            title="Cofnij odrzucenie — przywróć zapotrzebowanie do stanu sprzed odrzucenia"
                            loading={reopenMutation.isPending && reopenMutation.variables === req.id}
                            onClick={() => reopenMutation.mutate(req.id)}
                          >
                            Przywróć
                          </Button>
                        )}
                        <Link
                          to={`/requisitions/${req.id}`}
                          className="text-xs text-[var(--color-primary)] hover:underline"
                        >
                          Szczegóły
                        </Link>
                        {req.status === "approved" && req.order_id && (
                          <Link
                            to={`/orders/${req.order_id}`}
                            className="text-xs text-green-600 hover:underline"
                          >
                            Zamówienie
                          </Link>
                        )}
                        {canDelete && !req.order_id && (
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => setDeleteConfirmId(req.id)}
                          >
                            Usuń
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
          {/* One footer for both layouts — they are mutually exclusive by breakpoint. */}
          <LoadMoreButton
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={() => fetchNextPage()}
            onLoadAll={fetchAll}
            isFetchingAll={isFetchingAll}
            loaded={visible.length}
            total={total}
          />
        </>
      )}
      {deleteConfirmId && (
        <Modal open title="Usuń zapotrzebowanie" onClose={() => setDeleteConfirmId(null)} size="sm">
          <div className="flex flex-col gap-4">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Czy na pewno chcesz usunąć to zapotrzebowanie? Tej operacji nie można cofnąć.
            </p>
            {deleteMutation.isError && (
              <p className="text-sm text-red-600">
                {(deleteMutation.error as { response?: { data?: { errors?: { detail?: string } } } })
                  ?.response?.data?.errors?.detail ?? "Błąd usunięcia. Spróbuj ponownie."}
              </p>
            )}
            <div className="flex gap-3">
              <Button
                variant="danger"
                loading={deleteMutation.isPending}
                onClick={() =>
                  deleteMutation.mutate(deleteConfirmId, { onSuccess: () => setDeleteConfirmId(null) })
                }
              >
                Usuń
              </Button>
              <Button variant="secondary" onClick={() => setDeleteConfirmId(null)}>
                Anuluj
              </Button>
            </div>
          </div>
        </Modal>
      )}
      <RequisitionApproveModal target={approveModal} onClose={() => setApproveModal(null)} />
    </div>
  );
}
